import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, Users, Building2, Heart, Landmark } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import type { ApplicantType } from '@/components/profile/ApplicantTypeSelector';
import { useLanguage } from '@/contexts/language';
import { ProfileCardMenu } from '@/components/profile/ProfileCardMenu';

interface Profile {
  id: string;
  profile_name: string;
  applicant_type: ApplicantType;
  created_at: string;
  updated_at: string;
  profile_data?: any;
}

interface ProfilesListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateNew: () => void;
  suspendedOnly?: boolean;
}

const getTypeIcon = (type: ApplicantType) => {
  const icons = {
    individual: Users,
    business: Building2,
    nonprofit: Heart,
    municipality: Landmark,
  };
  return icons[type] || Users;
};

const getTypeLabel = (type: ApplicantType, t: any) => {
  const labels = {
    individual: t('common.individual'),
    business: t('common.business'),
    nonprofit: t('common.nonprofit'),
    municipality: t('common.municipality'),
  };
  return labels[type] || type;
};

export const ProfilesListDialog = ({
  open,
  onOpenChange,
  onCreateNew,
  suspendedOnly = false,
}: ProfilesListDialogProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch all profiles when dialog opens
  useEffect(() => {
    if (open && user) {
      fetchProfiles();
    }
  }, [open, user, suspendedOnly]);

  const fetchProfiles = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('applicant_profiles')
      .select('*')
      .eq('user_id', user?.id)
      .eq('is_active', suspendedOnly ? false : true)
      .order('updated_at', { ascending: false });

    if (!error && data) {
      setProfiles(data);
    }
    setLoading(false);
  };

  // Filter profiles based on search query
  const filteredProfiles = profiles.filter((profile) =>
    profile.profile_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleProfileClick = (profileId: string) => {
    onOpenChange(false);
    navigate(`/profile/${profileId}`);
  };

  const handleCreateNew = () => {
    onOpenChange(false);
    onCreateNew();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] p-0 border-[#e2e8f0]">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#e2e8f0]">
          <DialogTitle className="text-2xl font-bold tracking-tight text-[#1a1a2e]">
            {suspendedOnly ? t('common.yourSuspendedProfiles') : t('common.yourProfiles')}
          </DialogTitle>
          <div className="flex items-center justify-between mt-2">
            <DialogDescription className="text-[#64748b]">
              {profiles.length === 0
                ? (suspendedOnly ? t('common.noSuspendedProfiles') : t('common.noProfilesYet'))
                : t(suspendedOnly
                    ? (profiles.length > 1 ? 'common.profilesSuspended_plural' : 'common.profilesSuspended')
                    : (profiles.length > 1 ? 'common.profilesAvailable_plural' : 'common.profilesAvailable'),
                    { count: profiles.length })}
            </DialogDescription>
            {!suspendedOnly && (
              <Button
                onClick={handleCreateNew}
                className="gap-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] hover:shadow-[0_4px_15px_rgba(102,126,234,0.4)] text-white shadow-sm"
                size="sm"
              >
                <Plus className="h-4 w-4" />
                {t('common.createNewProfile')}
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Search Bar */}
        <div className="px-6 py-3 border-b border-[#e2e8f0]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#64748b]" />
            <Input
              placeholder={t('forms.placeholders.searchProfile')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 border-[#e2e8f0] focus:border-[#667eea] focus:ring-[#667eea]"
            />
          </div>
        </div>

        {/* Profiles List */}
        <div className="px-6 py-4 flex flex-col">
          <ScrollArea className="h-[400px] pr-4">
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-20 animate-pulse bg-[#f8fafc] rounded-xl"
                  />
                ))}
              </div>
            ) : filteredProfiles.length > 0 ? (
              <div className="space-y-2">
                {filteredProfiles.map((profile) => {
                  const TypeIcon = getTypeIcon(profile.applicant_type);
                  const companyLogo = profile.profile_data?.company_logo;

                  return (
                    <div
                      key={profile.id}
                      className="w-full p-4 rounded-xl border border-[#e2e8f0] bg-white hover:bg-[#f8fafc] hover:border-[#667eea] hover:shadow-md transition-all duration-200 group"
                    >
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/profile/${profile.id}`}
                          onClick={(e) => {
                            // Close dialog on regular click, allow Ctrl+Click to open new tab
                            if (!e.ctrlKey && !e.metaKey) {
                              onOpenChange(false);
                            }
                          }}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                        >
                          <div className="flex-shrink-0 p-2 bg-[#f8fafc] rounded-lg group-hover:bg-white transition-colors border border-[#e2e8f0]">
                            {companyLogo ? (
                              <img
                                src={companyLogo}
                                alt={`${profile.profile_name} logo`}
                                className="h-8 w-8 object-contain rounded"
                              />
                            ) : (
                              <TypeIcon className="h-5 w-5 text-[#64748b]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-[#1a1a2e] truncate group-hover:text-[#667eea]">
                              {profile.profile_name}
                            </h3>
                            <Badge
                              variant="outline"
                              className="mt-1 text-xs border-[#e2e8f0] text-[#64748b]"
                            >
                              {getTypeLabel(profile.applicant_type, t)}
                            </Badge>
                          </div>
                          <div className="text-xs text-[#64748b]">
                            {t('common.updated')}:{' '}
                            {new Date(profile.updated_at).toLocaleDateString(undefined, {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </div>
                        </Link>
                        <div className="flex-shrink-0">
                          <ProfileCardMenu
                            profileId={profile.id}
                            profileName={profile.profile_name}
                            onCategoryEdit={() => {
                              onOpenChange(false);
                              navigate(`/profile/${profile.id}/edit`);
                            }}
                            onSuspendSuccess={() => {
                              // Close the dialog to trigger a fresh fetch when reopened
                              onOpenChange(false);
                            }}
                            isSuspended={suspendedOnly}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="p-4 bg-[#f8fafc] rounded-xl mb-4 border border-[#e2e8f0]">
                  <Users className="h-8 w-8 text-[#64748b]" />
                </div>
                <h3 className="text-lg font-semibold text-[#1a1a2e] mb-2">
                  {searchQuery ? t('common.noProfileFound') : t('common.noProfiles')}
                </h3>
                <p className="text-sm text-[#64748b] mb-4">
                  {searchQuery
                    ? t('common.tryAnotherSearch')
                    : t('common.createFirstProfile')}
                </p>
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};
